import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import {
	formatSize,
	getPriceDigits,
	toNumber,
} from "../../../lib/formatters.ts";
import { MIN_ORDER_NOTIONAL_USD } from "../../../lib/trade-constants.ts";
import {
	buildLeverageMarks,
	DEFAULT_LEVERAGE_VALUE,
	isMarketOrderType,
	isTriggerOrderType,
	isTwapOrderType,
	type MarginMode,
	needsLimitPrice,
	type OrderType,
} from "../constants/order-form.constants.ts";
import {
	type AmountInputMode,
	amountInputModeAtom,
	autoQuoteAtom,
	getStoredLeverage,
	hasInitFromApiAtom,
	leverageIndexAtom,
	marginModeAtom,
	orderDraftAtom,
	orderSideAtom,
	orderTypeAtom,
	reduceOnlyAtom,
	setStoredLeverage,
	timeInForceAtom,
	triggerPriceAtom,
	twapDurationAtom,
	twapNumSlicesAtom,
} from "../stores/order-form-atoms.ts";
import { deriveOrderValues } from "./derive-order-values.ts";
import { createOrderDraft } from "./order-form-model.ts";

export type { AmountInputMode } from "../stores/order-form-atoms.ts";

type OrderFormStateParams = {
	accountAddress?: string | null;
	coin: string;
	markPrice: number;
	maxLeverage: number;
	szDecimals: number;
	withdrawable: string | number | null | undefined;
	availableToTrade?: readonly [string, string];
	maxTradeSzs?: readonly [string, string];
	isXyzMarket?: boolean;
	isUnified?: boolean;
	isDexAbstraction?: boolean;
	accountValue: string | number | null | undefined;
	perpAccountValue: string | number | null | undefined;
	currentPositionSize: string | number | null | undefined;
	currentPositionEntryPx: string | number | null | undefined;
	currentPositionLiquidationPx: string | number | null | undefined;
	currentPositionLeverage?: { type: string; value: number } | null;
	selectedPrice?: string;
	bestBid?: string;
	bestAsk?: string;
	isCrossMarginSupported?: boolean;
	defaultMarginMode: MarginMode;
};

function leverageToIndex(leverage: number, marks: number[]): number {
	const exact = marks.indexOf(leverage);
	if (exact !== -1) return exact;
	let closest = 0;
	for (let i = 1; i < marks.length; i++) {
		if (Math.abs(marks[i] - leverage) < Math.abs(marks[closest] - leverage)) {
			closest = i;
		}
	}
	return closest;
}

export function useOrderFormState({
	accountAddress,
	coin,
	markPrice,
	maxLeverage,
	szDecimals,
	withdrawable,
	availableToTrade,
	maxTradeSzs,
	isXyzMarket = false,
	isUnified = false,
	isDexAbstraction = false,
	accountValue,
	perpAccountValue,
	currentPositionSize,
	currentPositionEntryPx,
	currentPositionLiquidationPx,
	currentPositionLeverage,
	selectedPrice,
	bestBid,
	bestAsk,
	isCrossMarginSupported = true,
	defaultMarginMode,
}: OrderFormStateParams) {
	// ── Jotai atoms (shared across components) ──────────────────────
	const [autoQuote, setStoredAutoQuote] = useAtom(autoQuoteAtom);
	const [amountInputMode, setAmountInputMode] = useAtom(amountInputModeAtom);
	const [orderType, setStoredOrderType] = useAtom(orderTypeAtom);
	const [timeInForce, setTimeInForce] = useAtom(timeInForceAtom);
	const [side, setSide] = useAtom(orderSideAtom);
	const [draft, setDraft] = useAtom(orderDraftAtom);
	const [selectedLeverageIndex, setSelectedLeverageIndex] =
		useAtom(leverageIndexAtom);
	const [reduceOnly, setReduceOnly] = useAtom(reduceOnlyAtom);
	const [marginMode, setMarginMode] = useAtom(marginModeAtom);
	const [triggerPrice, setTriggerPrice] = useAtom(triggerPriceAtom);
	const [twapDuration, setTwapDuration] = useAtom(twapDurationAtom);
	const [twapNumSlices, setTwapNumSlices] = useAtom(twapNumSlicesAtom);
	const [hasInitFromApi, setHasInitFromApi] = useAtom(hasInitFromApiAtom);

	// ── Derived constants ───────────────────────────────────────────
	const leverageMarks = buildLeverageMarks(maxLeverage);
	const showTriggerPrice = isTriggerOrderType(orderType);
	const showLimitPrice = needsLimitPrice(orderType);
	const showTwapFields = isTwapOrderType(orderType);
	const showTimeInForce = orderType === "Limit order";
	const autoQuotePrice = side === "buy" ? bestBid : bestAsk;
	const autoQuoteAvailable = showLimitPrice && Boolean(autoQuotePrice);
	const autoQuoteActive = autoQuote && autoQuoteAvailable;

	// ── Local refs for coin-change detection ────────────────────────
	const [prevCoin, setPrevCoin] = useState(coin);
	const [prevAccountAddress, setPrevAccountAddress] = useState(accountAddress);
	const lastAppliedSelectedPriceRef = useRef<string | null>(null);
	const shouldSeedPriceRef = useRef(true);

	function resetDraft(nextOrderType = orderType) {
		shouldSeedPriceRef.current = true;
		setDraft(
			createOrderDraft(
				amountInputMode,
				autoQuote && needsLimitPrice(nextOrderType) && Boolean(autoQuotePrice),
			),
		);
	}

	function setAutoQuote(nextValue: boolean) {
		if (nextValue) {
			shouldSeedPriceRef.current = false;
		}
		setStoredAutoQuote(nextValue);
		setDraft((prev) => ({
			...prev,
			price: {
				...prev.price,
				source: nextValue && autoQuoteAvailable ? "autoLast" : "manual",
				manualInput:
					!nextValue && prev.price.source === "autoLast"
						? (autoQuotePrice ?? prev.price.manualInput)
						: prev.price.manualInput,
			},
		}));
	}

	function setOrderType(next: OrderType) {
		setStoredOrderType(next);
		resetDraft(next);
		setTriggerPrice("");
	}

	// ── Coin/account change → reset form fields ────────────────────
	// Leverage/marginMode init is handled in the useEffect below, gated on
	// real market data (maxLeverage > 0). Keeping that logic out of this
	// in-render block avoids two bugs: (1) prior coin's leverage index
	// bleeding into a new coin that has no stored/position value, and
	// (2) a race where maxLeverage=0 from createEmptyMarketData collapses
	// the mapping to index 0 and never recovers.
	if (prevCoin !== coin || prevAccountAddress !== accountAddress) {
		setPrevCoin(coin);
		setPrevAccountAddress(accountAddress);
		resetDraft();
		setTriggerPrice("");
		setHasInitFromApi(false);
		lastAppliedSelectedPriceRef.current = null;
	}

	// Display-safe index: clamp to valid range even when marks is empty.
	const safeSelectedLeverageIndex =
		leverageMarks.length > 0
			? Math.min(Math.max(selectedLeverageIndex, 0), leverageMarks.length - 1)
			: 0;

	// Only write the clamped value back when market data is real — otherwise
	// marks=[0] pre-load would pin the atom to 0 and the real data arriving
	// later wouldn't un-pin it.
	if (maxLeverage > 0 && safeSelectedLeverageIndex !== selectedLeverageIndex) {
		setSelectedLeverageIndex(safeSelectedLeverageIndex);
	}

	useEffect(() => {
		if (marginMode === "Cross" && !isCrossMarginSupported) {
			setMarginMode(defaultMarginMode);
		}
	}, [defaultMarginMode, isCrossMarginSupported, marginMode, setMarginMode]);

	// Initialize leverage + margin mode once market data is available for
	// this (account, coin). Runs at most once per (account, coin) — if the
	// user has a position, the next effect (currentPositionLeverage sync)
	// overrides this with the on-chain value.
	const leverageInitKeyRef = useRef<string | null>(null);
	useEffect(() => {
		if (maxLeverage <= 0) return;
		const key = `${accountAddress ?? ""}:${coin}`;
		if (leverageInitKeyRef.current === key) return;
		leverageInitKeyRef.current = key;

		const marks = buildLeverageMarks(maxLeverage);
		const stored = getStoredLeverage(accountAddress, coin);

		if (stored) {
			setSelectedLeverageIndex(leverageToIndex(stored.leverage, marks));
			setMarginMode(stored.marginMode);
		} else {
			setSelectedLeverageIndex(leverageToIndex(DEFAULT_LEVERAGE_VALUE, marks));
			setMarginMode(defaultMarginMode);
		}
	}, [
		accountAddress,
		coin,
		maxLeverage,
		defaultMarginMode,
		setMarginMode,
		setSelectedLeverageIndex,
	]);

	useEffect(() => {
		if (hasInitFromApi || !currentPositionLeverage) return;
		const nextMarginMode: MarginMode =
			currentPositionLeverage.type === "isolated"
				? "Isolated"
				: defaultMarginMode;
		setMarginMode(nextMarginMode);
		setSelectedLeverageIndex(
			leverageToIndex(currentPositionLeverage.value, leverageMarks),
		);
		setHasInitFromApi(true);
		setStoredLeverage(accountAddress, coin, {
			leverage: currentPositionLeverage.value,
			marginMode: nextMarginMode,
		});
	}, [
		accountAddress,
		coin,
		currentPositionLeverage,
		defaultMarginMode,
		hasInitFromApi,
		leverageMarks,
		setHasInitFromApi,
		setMarginMode,
		setSelectedLeverageIndex,
	]);

	function commitLeverageSelection(nextIndex: number) {
		setSelectedLeverageIndex(nextIndex);
		const leverageValue = leverageMarks[nextIndex] ?? leverageMarks[0];
		setStoredLeverage(accountAddress, coin, {
			leverage: leverageValue,
			marginMode,
		});
	}

	function commitMarginMode(nextMode: MarginMode) {
		setMarginMode(nextMode);
		setStoredLeverage(accountAddress, coin, {
			leverage: leverageMarks[safeSelectedLeverageIndex] ?? leverageMarks[0],
			marginMode: nextMode,
		});
	}

	const selectedLeverage = leverageMarks[safeSelectedLeverageIndex];

	// HL's `activeAssetData.availableToTrade` is server-computed, side-aware
	// ([buy, sell] — verified: with a long open, the sell entry exceeds buy by
	// exactly 2×|szi|) and per-coin — accounts for closing/flipping the existing
	// position and resting-order reservations. It is the EXACT margin boundary
	// with zero fee/drift headroom (maxTradeSz == availableToTrade × lev ÷ mark
	// to the last decimal); the leverage-scaled cost reserve that keeps a 100%
	// order from bouncing lives in `deriveOrderValues` (MAX_SIZE_COST_RATE).
	//
	// Exception: on an xyz (HIP-3) market, a *manual* account (userAbstraction is
	// neither `unifiedAccount` nor `dexAbstraction`) has no collateral usable on
	// the xyz DEX, so HL reports near-zero **dust** (e.g. 0.0088, NOT exactly 0).
	// Unified / dexAbstraction accounts get the correct per-coin value from HL
	// directly, so they must NOT be overridden. For the manual case, show
	// max(xyzCapacity, main `withdrawable`) so the dust isn't displayed — the main
	// collateral becomes usable on the first xyz order (auto dexAbstraction in
	// `usePlaceOrder`), and it self-corrects once abstracted. `withdrawable` is
	// also the loading fallback for all markets.
	const sideIdx = side === "buy" ? 0 : 1;
	const usingXyzFallback = isXyzMarket && !isUnified && !isDexAbstraction;
	const sideAwareCapacity = availableToTrade
		? toNumber(availableToTrade[sideIdx])
		: null;
	const availableBalance =
		sideAwareCapacity === null
			? toNumber(withdrawable)
			: usingXyzFallback
				? Math.max(sideAwareCapacity, toNumber(withdrawable))
				: sideAwareCapacity;
	// HL margins orders at MARK, so the server's side-aware `maxTradeSzs` is the
	// true size cap (a below-mark limit price affords fewer coins than
	// notional/limitPx suggests). The xyz manual-account fallback above
	// deliberately overrides HL's dust capacity, so it must not be re-capped by
	// the equally-dust maxTradeSzs.
	const maxSizeCap =
		maxTradeSzs && sideAwareCapacity !== null && !usingXyzFallback
			? toNumber(maxTradeSzs[sideIdx])
			: null;

	const derived = deriveOrderValues({
		draft,
		markPrice,
		autoQuotePrice,
		selectedLeverage,
		availableBalance,
		szDecimals,
		side,
		currentPositionLiquidationPx,
		orderType,
		marginMode,
		maxLeverage,
		perpAccountValue,
		currentPositionSize,
		currentPositionEntryPx,
		maxSizeCap,
	});

	// ── Auto-quote sync ─────────────────────────────────────────────
	useEffect(() => {
		setDraft((prev) => {
			if (autoQuoteActive) {
				if (prev.price.source === "autoLast") return prev;
				return {
					...prev,
					price: { ...prev.price, source: "autoLast" },
				};
			}
			if (prev.price.source !== "autoLast") return prev;
			return {
				...prev,
				price: {
					...prev.price,
					source: "manual",
					manualInput: autoQuotePrice ?? prev.price.manualInput,
				},
			};
		});
	}, [autoQuoteActive, autoQuotePrice, setDraft]);

	// ── Seed price with markPrice on first load ────────────────────
	useEffect(() => {
		if (!markPrice || autoQuoteActive || !showLimitPrice) return;
		if (!shouldSeedPriceRef.current) return;

		const digits = getPriceDigits(markPrice);
		const seededPrice = markPrice.toFixed(digits);

		setDraft((prev) => {
			if (
				prev.price.source === "autoLast" ||
				prev.price.manualInput ||
				prev.price.selectedInput
			) {
				shouldSeedPriceRef.current = false;
				return prev;
			}

			shouldSeedPriceRef.current = false;
			return {
				...prev,
				price: {
					...prev.price,
					source: "manual",
					manualInput: seededPrice,
				},
			};
		});
	}, [
		autoQuoteActive,
		draft.price.manualInput,
		draft.price.selectedInput,
		draft.price.source,
		markPrice,
		setDraft,
		showLimitPrice,
	]);

	// ── Selected price from orderbook click ─────────────────────────
	useEffect(() => {
		if (autoQuoteActive || !selectedPrice) return;
		if (lastAppliedSelectedPriceRef.current === selectedPrice) return;
		lastAppliedSelectedPriceRef.current = selectedPrice;
		shouldSeedPriceRef.current = false;
		setDraft((prev) => ({
			...prev,
			price: {
				...prev.price,
				source: "selected",
				selectedInput: selectedPrice,
			},
		}));
	}, [autoQuoteActive, selectedPrice, setDraft]);

	// ── Input handlers ──────────────────────────────────────────────
	function handleQuoteAmountChange(nextAmount: string) {
		setAmountInputMode("quoteAmount");
		setDraft((prev) => ({
			...prev,
			size: { source: "quote", input: nextAmount },
		}));
	}

	function handlePriceChange(nextPrice: string) {
		shouldSeedPriceRef.current = false;
		setDraft((prev) => ({
			...prev,
			price: { ...prev.price, source: "manual", manualInput: nextPrice },
		}));
	}

	function handleAmountChange(nextAmount: string) {
		setAmountInputMode("amount");
		setDraft((prev) => ({
			...prev,
			size: { source: "base", input: nextAmount },
		}));
	}

	function handleAmountPercentageChange(pct: number) {
		setDraft((prev) => ({
			...prev,
			size: { source: "percent", input: String(pct) },
		}));
	}

	function handleAmountInputModeChange(mode: AmountInputMode) {
		setAmountInputMode(mode);
	}

	// ── Submit logic ────────────────────────────────────────────────
	const submitLabel = accountAddress
		? side === "buy"
			? "order.side.buy"
			: "order.side.sell"
		: "actions.connect";

	const isMarket = isMarketOrderType(orderType);
	const isTrigger = isTriggerOrderType(orderType);
	const isTwap = isTwapOrderType(orderType);
	const hasTrigger = !isTrigger || toNumber(triggerPrice) > 0;

	const belowMinNotional =
		derived.hasSize &&
		derived.orderValue > 0 &&
		derived.orderValue < MIN_ORDER_NOTIONAL_USD;
	const canSubmit =
		derived.hasPrice &&
		derived.hasSize &&
		hasTrigger &&
		!belowMinNotional &&
		!derived.aboveMaxNotional;

	const orderParams = {
		coin,
		side,
		orderType,
		price: isMarket
			? Number(
					(markPrice * (side === "buy" ? 1.01 : 0.99)).toPrecision(5),
				).toString()
			: derived.displayPrice,
		size: derived.sizeNum.toFixed(szDecimals),
		reduceOnly,
		timeInForce,
		triggerPrice,
		markPrice,
		twapDuration: isTwap ? twapDuration : undefined,
		twapNumSlices: isTwap ? twapNumSlices : undefined,
	};

	function resetInputs() {
		resetDraft();
	}

	return {
		resetInputs,
		side,
		setSide,
		orderType,
		setOrderType,
		price: derived.displayPrice,
		setPrice: handlePriceChange,
		amount: derived.displayAmount,
		setAmount: handleAmountChange,
		quoteAmount: derived.displayQuoteAmount,
		setQuoteAmount: handleQuoteAmountChange,
		leverageMarks,
		selectedLeverageIndex: safeSelectedLeverageIndex,
		setSelectedLeverageIndex: commitLeverageSelection,
		selectedLeverage,
		reduceOnly,
		setReduceOnly,
		timeInForce,
		setTimeInForce,
		marginMode,
		setMarginMode: commitMarginMode,
		triggerPrice,
		setTriggerPrice,
		twapDuration,
		setTwapDuration,
		twapNumSlices,
		setTwapNumSlices,
		amountPercentage: derived.displayPercentage,
		setAmountPercentage: handleAmountPercentageChange,
		autoQuote,
		autoQuoteActive,
		setAutoQuote,
		autoQuoteAvailable,
		amountInputMode,
		setAmountInputMode: handleAmountInputModeChange,
		showTriggerPrice,
		showLimitPrice,
		showTwapFields,
		showTimeInForce,
		orderValue: derived.orderValue,
		marginRequired: derived.marginRequired,
		estimatedLiquidation: derived.estimatedLiquidation,
		submitLabel,
		availableFunds: `${formatSize(availableBalance, 2)} USDC`,
		hasAvailableFunds: toNumber(accountValue) > 0,
		currentPositionLabel: `${formatSize(currentPositionSize, 5)} ${coin}`,
		canSubmit,
		belowMinNotional,
		aboveMaxNotional: derived.aboveMaxNotional,
		orderParams,
	};
}
